import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signup } from '../../shared/api/auth';
import { ApiError } from '../../shared/api/client';
import { useAuthStore } from '../../shared/hooks/useAuthStore';
import { ALLOWED_INVITE_DOMAINS, isAllowedEmail } from '@shared/contracts/invite';

/**
 * Self-signup page - fully static. Same minimal background as LoginPage:
 * one CSS gradient, no motion, no cursor tracking, no backdrop-filter.
 */
export function SignupPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Hosted-mode signup returns 202 with `pendingVerification: true` instead
  // of issuing tokens - the user must click the link in their email before
  // signing in. We flip the page into a "check your email" state in that case.
  const [pendingVerification, setPendingVerification] = useState<string | null>(null);

  // Live password feedback. Mirrors the server schema so the user knows
  // before submitting whether the password will pass.
  const pwLong = password.length >= 12;
  const pwUpper = /[A-Z]/.test(password);
  const pwLower = /[a-z]/.test(password);
  const pwDigit = /[0-9]/.test(password);
  const pwValid = pwLong && pwUpper && pwLower && pwDigit;
  const pwMatch = password.length > 0 && password === password2;
  const emailValid = email.length === 0 || isAllowedEmail(email);

  const canSubmit =
    displayName.trim().length > 0 &&
    isAllowedEmail(email) &&
    pwValid &&
    pwMatch &&
    !busy;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const normalisedEmail = email.trim().toLowerCase();
      const res = await signup({
        email: normalisedEmail,
        displayName: displayName.trim(),
        password,
        rememberMe,
      });
      if (res.pendingVerification) {
        // Hosted mode - user must verify their email before signing in.
        setPendingVerification(normalisedEmail);
      } else {
        setUser(res.user);
        navigate('/', { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) setError(err.body.message);
      else if (err instanceof Error) setError(err.message);
      else setError('Sign-up failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="relative min-h-full w-full flex items-center justify-center p-6"
      style={{
        background:
          'linear-gradient(135deg, #0f172a 0%, #11233f 55%, #0e2a3a 100%)',
      }}
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900 p-6 shadow-soft-lg">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-lg bg-gradient-brand text-white flex items-center justify-center font-semibold">
            T
          </div>
          <div>
            <div className="text-base font-semibold text-slate-100">
              Tricorian CRM
            </div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-accent-300/80">
              Create account
            </div>
          </div>
        </div>

        {pendingVerification ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-200">
              Account created - we&apos;ve emailed a verification link to{' '}
              <span className="font-medium">{pendingVerification}</span>. Click
              the link in your inbox to finish setup, then sign in.
            </div>
            <p className="text-[11px] text-slate-500">
              The link is valid for 24 hours. Check your spam folder if it
              doesn&apos;t arrive in a couple of minutes.
            </p>
            <Link to="/login" className="btn-primary w-full inline-flex justify-center">
              Go to sign in
            </Link>
          </div>
        ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <p className="text-xs text-slate-400">
            Restricted to{' '}
            <span className="text-accent-300/90 font-medium">
              {ALLOWED_INVITE_DOMAINS.join(', ')}
            </span>
            . Use your work email.
          </p>

          <label className="block">
            <div className="label mb-1">Full name</div>
            <input
              className="input"
              autoComplete="name"
              placeholder="e.g. Jane Smith"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </label>

          <label className="block">
            <div className="label mb-1">Work email</div>
            <input
              type="email"
              autoComplete="username"
              className="input"
              placeholder="name@tricorian.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {!emailValid && (
              <p className="text-[11px] text-rose-400 mt-1">
                Email must end in @{ALLOWED_INVITE_DOMAINS[0]}.
              </p>
            )}
          </label>

          <label className="block">
            <div className="label mb-1">Password</div>
            <input
              type="password"
              autoComplete="new-password"
              className="input"
              placeholder="At least 12 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <ul className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
              <PwRule ok={pwLong} label="≥ 12 characters" />
              <PwRule ok={pwUpper} label="Uppercase letter" />
              <PwRule ok={pwLower} label="Lowercase letter" />
              <PwRule ok={pwDigit} label="A digit" />
            </ul>
          </label>

          <label className="block">
            <div className="label mb-1">Confirm password</div>
            <input
              type="password"
              autoComplete="new-password"
              className="input"
              placeholder="Re-type your password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              required
            />
            {password2.length > 0 && !pwMatch && (
              <p className="text-[11px] text-rose-400 mt-1">
                Passwords don&apos;t match.
              </p>
            )}
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-300 select-none cursor-pointer">
            <input
              type="checkbox"
              className="accent-brand-500"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>
              Remember me on this device
              <span className="block text-[11px] text-slate-500">
                Keeps you signed in for 30 days.
              </span>
            </span>
          </label>

          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        )}

        <p className="mt-5 text-xs text-slate-400 text-center">
          Already have an account?{' '}
          <Link to="/login" className="link-brand">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function PwRule({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li
      className={
        ok ? 'text-emerald-400' : 'text-slate-500'
      }
    >
      <span className="mr-1">{ok ? '✓' : '○'}</span>
      {label}
    </li>
  );
}
